import React, { useCallback, useMemo } from "react";

import {
    AppBar,
    Authenticator,
    CMSView,
    CircularProgressCenter,
    Drawer,
    FireCMS,
    FireCMSi18nProvider,
    ModeControllerProvider,
    NavigationRoutes,
    Scaffold,
    SideDialogs,
    SnackbarProvider,
    useBuildLocalConfigurationPersistence,
    useBuildModeController,
    useBuildNavigationController,
    useValidateAuthenticator
} from "@firecms/core";
import {
    FirebaseAuthController,
    FirebaseLoginView,
    FirebaseSignInProvider,
    FirebaseUserWrapper,
    useFirebaseAuthController,
    useFirebaseStorageSource,
    useFirestoreDelegate,
    useInitialiseFirebase
} from "@firecms/firebase";
import { Alert, CenteredView, Chip } from "@firecms/ui";

import { RoleAdminView } from "./admin";
import { guidesCollection } from "./collections/guides";
import {
    authorsCollection,
    categoriesCollection,
    claimsCollection,
    countriesCollection,
    hubConfigCollection,
    paymentRailsCollection,
    reviewersCollection,
    sourcesCollection
} from "./collections/supporting";
import { DashboardsView } from "./dashboards";
import { configError as readConfigError, connectEmulators, firebaseConfig, useEmulators } from "./firebase";
import { CmsAuthExtra, isRole, roleOf } from "./roles";

function App() {

    /**
     * Access control, as far as the browser is concerned.
     *
     * The role lives in a Firebase custom claim because that is the only thing
     * Firestore Security Rules can read (`request.auth.token.role`). Reading it
     * here and stashing it on the auth controller lets the collections hide what
     * a role cannot use. Hiding is all this does: the rules are what refuse.
     *
     * A user with no role is turned away rather than shown an empty CMS. Every
     * rule in firestore.rules denies them anyway, so letting them in would only
     * produce a screen of permission errors instead of an explanation.
     */
    const authenticator: Authenticator<FirebaseUserWrapper> = useCallback(async ({
                                                                                    user,
                                                                                    authController
                                                                                }) => {
        if (!user) return false;

        const token = await user.firebaseUser?.getIdTokenResult();
        const role = token?.claims?.role;

        if (!isRole(role)) {
            throw new Error(
                `${user.email ?? "This account"} has no CMS role. An admin has to assign one ` +
                `with the setUserRole function before you can sign in.`
            );
        }

        authController.setExtra({ role } satisfies CmsAuthExtra);
        return true;
    }, []);

    const collections = useMemo(() => [
        guidesCollection,
        categoriesCollection,
        hubConfigCollection,
        sourcesCollection,
        claimsCollection,
        paymentRailsCollection,
        countriesCollection,
        authorsCollection,
        reviewersCollection
    ], []);

    const {
        firebaseApp,
        firebaseConfigLoading,
        configError
    } = useInitialiseFirebase({
        firebaseConfig,
        onFirebaseInit: (_config, app) => {
            if (useEmulators) connectEmulators(app);
        }
    });

    const modeController = useBuildModeController();

    /* Password only. Google sign-in cannot work against the Auth emulator
       without a real OAuth client, and adding a provider that only works in
       production would make local development diverge from the thing being
       tested. */
    const signInOptions: FirebaseSignInProvider[] = ["password"];

    const authController: FirebaseAuthController = useFirebaseAuthController({
        firebaseApp,
        signInOptions
    });

    /**
     * The role screen (ADR 001).
     *
     * Hidden rather than omitted for non-admins, so the entry disappears from the
     * drawer without the route vanishing under someone mid-navigation. Hiding is
     * cosmetic either way: the view refuses to render for a non-admin, and
     * `setUserRole` refuses the call, which is the check that counts.
     */
    const views: CMSView[] = useMemo(() => {
        if (!firebaseApp) return [];
        return [{
            /* Visible to every role, including `viewer`. It reads what its
               reader can already read and writes nothing, and the questions it
               answers — what is overdue, what is stuck, what failed to publish
               — are exactly the ones somebody without edit rights is most
               likely to be asking. */
            path: "dashboards",
            name: "Dashboards",
            icon: "insights",
            description:
                "Overview, review queues, gate warnings, broken links, and publish health.",
            group: "Insights",
            view: <DashboardsView/>
        }, {
            path: "user-roles",
            name: "User roles",
            icon: "admin_panel_settings",
            description: "Assign CMS roles. Admin only.",
            group: "Admin",
            view: <RoleAdminView firebaseApp={firebaseApp}/>,
            hideFromNavigation: roleOf(authController) !== "admin"
        }];
    }, [firebaseApp, authController.extra]);

    const userConfigPersistence = useBuildLocalConfigurationPersistence();

    const firestoreDelegate = useFirestoreDelegate({
        firebaseApp,
        localTextSearchEnabled: true
    });

    const storageSource = useFirebaseStorageSource({ firebaseApp });

    const {
        authLoading,
        canAccessMainView,
        notAllowedError
    } = useValidateAuthenticator({
        authController,
        authenticator,
        dataSourceDelegate: firestoreDelegate,
        storageSource
    });

    const navigationController = useBuildNavigationController({
        disabled: authLoading,
        collections,
        views,
        authController,
        dataSourceDelegate: firestoreDelegate
    });

    const envError = readConfigError();
    if (envError) {
        return <CenteredView><Alert color={"error"}>{envError}</Alert></CenteredView>;
    }

    if (firebaseConfigLoading || !firebaseApp) {
        return <CircularProgressCenter/>;
    }

    if (configError) {
        return <CenteredView><Alert color={"error"}>{configError}</Alert></CenteredView>;
    }

    return (
        <FireCMSi18nProvider>
            <SnackbarProvider>
                <ModeControllerProvider value={modeController}>
                    <FireCMS
                        navigationController={navigationController}
                        authController={authController}
                        userConfigPersistence={userConfigPersistence}
                        dataSourceDelegate={firestoreDelegate}
                        storageSource={storageSource}
                    >
                        {({ loading }) => {

                            if (loading || authLoading) {
                                return <CircularProgressCenter size={"large"}/>;
                            }

                            if (!canAccessMainView) {
                                return <FirebaseLoginView authController={authController}
                                                          firebaseApp={firebaseApp}
                                                          signInOptions={signInOptions}
                                                          notAllowedError={notAllowedError}/>;
                            }

                            const role = roleOf(authController);

                            return <Scaffold autoOpenDrawer={false}>
                                <AppBar title={"Zennopay Knowledge Engine"}
                                        endAdornment={
                                            <div className={"flex flex-row gap-2 items-center"}>
                                                {useEmulators &&
                                                    <Chip size={"small"} colorScheme={"orangeLighter"}>
                                                        emulators
                                                    </Chip>}
                                                {role && <Chip size={"small"}>{role}</Chip>}
                                            </div>
                                        }/>
                                <Drawer/>
                                <NavigationRoutes/>
                                <SideDialogs/>
                            </Scaffold>;
                        }}
                    </FireCMS>
                </ModeControllerProvider>
            </SnackbarProvider>
        </FireCMSi18nProvider>
    );
}

export default App;
